import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 8) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-5 text-center">
      <div className="max-w-md fade-up" data-testid="payment-success-page">
        {state === "checking" && (<><Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" /><p className="mt-4 text-muted-foreground">Confirming your payment…</p></>)}
        {state === "paid" && (<>
          <div className="w-16 h-16 mx-auto grid place-items-center bg-primary text-primary-foreground mb-4"><Check className="w-8 h-8" /></div>
          <h1 className="text-3xl font-head font-black uppercase tracking-tighter">Payment Received</h1>
          <p className="mt-3 text-muted-foreground text-sm">Thank you! Your WM Window Tint appointment is booked and paid. We'll see you soon.</p>
        </>)}
        {(state === "error" || state === "timeout") && (<>
          <div className="w-16 h-16 mx-auto grid place-items-center bg-slate-200 text-slate-700 mb-4"><X className="w-8 h-8" /></div>
          <h1 className="text-2xl font-head font-black uppercase tracking-tighter">{state === "timeout" ? "Still Processing" : "Payment Not Confirmed"}</h1>
          <p className="mt-3 text-muted-foreground text-sm">{state === "timeout" ? "Your payment is still processing. If you were charged, your booking is confirmed. Please contact us if unsure." : "We couldn't confirm your payment. Your appointment is still saved — you can pay at the shop."}</p>
        </>)}
        <Button data-testid="payment-done-btn" onClick={() => navigate("/")} className="mt-6 rounded-none bg-primary text-primary-foreground font-bold uppercase">Back to Home</Button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-5 text-center">
      <div className="max-w-md fade-up" data-testid="payment-cancel-page">
        <div className="w-16 h-16 mx-auto grid place-items-center bg-slate-200 text-slate-700 mb-4"><X className="w-8 h-8" /></div>
        <h1 className="text-2xl font-head font-black uppercase tracking-tighter">Payment Cancelled</h1>
        <p className="mt-3 text-muted-foreground text-sm">No charge was made. Your appointment request is saved — you can rebook or pay at the shop.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button data-testid="payment-rebook-btn" onClick={() => navigate("/book")} variant="outline" className="rounded-none border-slate-300 bg-transparent">Book Again</Button>
          <Button onClick={() => navigate("/")} className="rounded-none bg-primary text-primary-foreground font-bold uppercase">Home</Button>
        </div>
      </div>
    </div>
  );
}
